import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shareTrialResults, saveTrialSettings } from '../../services/trialApi';

/**
 * Step 3: Validate, review, export, share, and plan next steps
 * Combined validation, results analysis, export, sharing, and next steps page
 */
const Step3Page = () => {
  const navigate = useNavigate();

  const [currentView, setCurrentView] = useState('validation');
  const [selectedClaim, setSelectedClaim] = useState(null);

  // Export Reports state
  const [selectedReports, setSelectedReports] = useState({
    executiveSummary: true,
    detailedAnalysis: true,
    validationReport: false,
    keyInsights: false
  });

  // Share Results state
  const [emailInput, setEmailInput] = useState('');
  const [sharedEmails, setSharedEmails] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Data Retention state
  const [dataRetention, setDataRetention] = useState('keep30days');
  const [selectedPlan, setSelectedPlan] = useState('professional');

  // Settings state
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Handler: Share results via email
  const handleShareResults = async () => {
    if (sharedEmails.length === 0) {
      setEmailError('Please add at least one email address');
      return;
    }

    setIsSendingEmail(true);
    setEmailError('');
    setEmailSuccess(false);

    try {
      const selectedReportTypes = Object.keys(selectedReports).filter(
        key => selectedReports[key]
      );

      await shareTrialResults(
        sharedEmails,
        selectedReportTypes,
        customMessage || null
      );

      setEmailSuccess(true);
      setEmailError('');

      setTimeout(() => {
        setEmailSuccess(false);
        setSharedEmails([]);
        setCustomMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error sharing results:', error);
      setEmailError(error.response?.data?.detail || 'Failed to send emails. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handler: Save settings
  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      await saveTrialSettings(dataRetention, selectedPlan);
      setSaveSuccess(true);
      setSaveError('');

      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveError(error.response?.data?.detail || 'Failed to save settings. Please try again.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Sample validation data
  const validationResults = {
    totalClaims: 8547,
    validClaims: 8245,
    warningClaims: 245,
    errorClaims: 57,
    successRate: 96.5
  };

  // Sample results data
  const resultsSummary = {
    approved: 6845,
    rejected: 1245,
    denied: 345,
    unprocessed: 112
  };

  return (
    <div className="step-page min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with back navigation */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/cdi-trial"
                className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium"
              >
                ← Back to CDI Trial
              </Link>
              <div className="h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Step 3: Validate, review, export, share, and plan next steps
              </h1>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              15 minutes
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">

          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Step 3 of 3</span>
              <span>100% Complete</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-primary-600 h-2 rounded-full" style={{ width: '100%' }}></div>
            </div>
          </div>

          {/* View toggle tabs */}
          <div className="mb-8">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setCurrentView('validation')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'validation'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  📊 Validation Results
                </button>
                <button
                  onClick={() => setCurrentView('results')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'results'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  📈 Analysis Results
                </button>
                <button
                  onClick={() => setCurrentView('export')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'export'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  📥 Export & Share
                </button>
                <button
                  onClick={() => setCurrentView('nextsteps')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    currentView === 'nextsteps'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  🎯 Next Steps
                </button>
              </nav>
            </div>
          </div>

          {/* Validation View */}
          {currentView === 'validation' && (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left side - Validation Summary */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Dataset Validation Summary
                  </h2>

                  {/* Validation metrics */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {validationResults.validClaims.toLocaleString()}
                      </div>
                      <div className="text-sm text-green-800 dark:text-green-200">Valid Claims</div>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {validationResults.warningClaims}
                      </div>
                      <div className="text-sm text-amber-800 dark:text-amber-200">Warnings</div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {validationResults.errorClaims}
                      </div>
                      <div className="text-sm text-red-800 dark:text-red-200">Errors</div>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {validationResults.successRate}%
                      </div>
                      <div className="text-sm text-blue-800 dark:text-blue-200">Success Rate</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Processing Progress</span>
                      <span>{validationResults.totalClaims.toLocaleString()} claims</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div className="bg-green-600 h-3 rounded-full" style={{ width: `${validationResults.successRate}%` }}></div>
                    </div>
                  </div>

                  {/* Download actions */}
                  <div className="space-y-3">
                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors">
                      📥 Download Error Report
                    </button>
                    <button className="w-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      📋 Download Remediation Tips
                    </button>
                  </div>
                </div>
              </div>

              {/* Right side - Error Details */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                    📋 What We'll Cover in This Step
                  </h3>
                  <ul className="space-y-2 text-sm" style={{ color: '#1F2937' }}>
                    <li>• Review validation results and status summary</li>
                    <li>• See totals by status: Approved, Rejected, Denied, Unprocessed</li>
                    <li>• View top reasons for Rejected/Denied and data quality notes</li>
                    <li>• Export reports and share via email</li>
                    <li>• Select data retention preferences and choose a plan</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                    ⚠️ Common Issues Found
                  </h3>
                  <div className="space-y-3 text-sm" style={{ color: '#1F2937' }}>
                    <div className="flex items-start space-x-2">
                      <div className="mt-0.5" style={{ color: '#1F2937' }}>•</div>
                      <div>
                        <div className="font-medium">Missing procedure codes (32 claims)</div>
                        <div className="text-xs">Some claims lack required CPT/HCPCS codes</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="mt-0.5" style={{ color: '#1F2937' }}>•</div>
                      <div>
                        <div className="font-medium">Date format inconsistencies (18 claims)</div>
                        <div className="text-xs">Mixed date formats detected in service dates</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="mt-0.5" style={{ color: '#1F2937' }}>•</div>
                      <div>
                        <div className="font-medium">Invalid NPI numbers (7 claims)</div>
                        <div className="text-xs">Provider NPI validation failed</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                    ✅ Validation Passed
                  </h3>
                  <div className="space-y-2 text-sm" style={{ color: '#1F2937' }}>
                    <p>Your dataset is ready for processing with minimal issues:</p>
                    <ul className="space-y-1 ml-4 text-xs">
                      <li>• 96.5% of claims passed validation</li>
                      <li>• No critical errors that would block processing</li>
                      <li>• Minor warnings can be addressed post-processing</li>
                      <li>• Data quality sufficient for meaningful analysis</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results View */}
          {currentView === 'results' && (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left side - Results Summary */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Claims Analysis Results
                  </h2>

                  {/* Results metrics */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {resultsSummary.approved.toLocaleString()}
                      </div>
                      <div className="text-sm text-green-800 dark:text-green-200">Approved</div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {resultsSummary.rejected.toLocaleString()}
                      </div>
                      <div className="text-sm text-red-800 dark:text-red-200">Rejected</div>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {resultsSummary.denied}
                      </div>
                      <div className="text-sm text-amber-800 dark:text-amber-200">Denied</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4">
                      <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                        {resultsSummary.unprocessed}
                      </div>
                      <div className="text-sm text-gray-800 dark:text-gray-200">Unprocessed</div>
                    </div>
                  </div>

                  {/* Top denial reasons */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Top Denial Reasons</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Missing prior authorization</span>
                        <span className="font-medium text-gray-900 dark:text-white">45%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Incorrect procedure code</span>
                        <span className="font-medium text-gray-900 dark:text-white">23%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Duplicate claim</span>
                        <span className="font-medium text-gray-900 dark:text-white">18%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Member eligibility</span>
                        <span className="font-medium text-gray-900 dark:text-white">14%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Detailed Analysis */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                    📊 Key Insights
                  </h3>
                  <div className="space-y-3 text-sm" style={{ color: '#1F2937' }}>
                    <div>
                      <div className="font-medium">Clean Claim Rate: 80.1%</div>
                      <div className="text-xs">Above industry average of 75%</div>
                    </div>
                    <div>
                      <div className="font-medium">Potential Revenue Recovery: $125,450</div>
                      <div className="text-xs">From addressable denials and rejections</div>
                    </div>
                    <div>
                      <div className="font-medium">Processing Time: 2.3 minutes</div>
                      <div className="text-xs">Average time per claim analysis</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                    🎯 Recommendations
                  </h3>
                  <div className="space-y-2 text-sm" style={{ color: '#1F2937' }}>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <span className="mt-0.5" style={{ color: '#1F2937' }}>•</span>
                        <span>Focus on prior authorization workflow improvements</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="mt-0.5" style={{ color: '#1F2937' }}>•</span>
                        <span>Implement procedure code validation at point of entry</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="mt-0.5" style={{ color: '#1F2937' }}>•</span>
                        <span>Enhance duplicate claim detection processes</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="mt-0.5" style={{ color: '#1F2937' }}>•</span>
                        <span>Real-time eligibility verification integration</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#1F2937' }}>
                    📋 Drill Down Options
                  </h3>
                  <div className="space-y-3">
                    <button className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="font-medium" style={{ color: '#1F2937' }}>View Rejected Claims Details</div>
                      <div className="text-sm" style={{ color: '#1F2937' }}>1,245 claims requiring attention</div>
                    </button>
                    <button className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="font-medium" style={{ color: '#1F2937' }}>Denied Claims Analysis</div>
                      <div className="text-sm" style={{ color: '#1F2937' }}>345 claims with denial patterns</div>
                    </button>
                    <button className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="font-medium" style={{ color: '#1F2937' }}>Data Quality Notes</div>
                      <div className="text-sm" style={{ color: '#1F2937' }}>Quality metrics and suggestions</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Export & Share View */}
          {currentView === 'export' && (
            <div className="space-y-8">
              {/* Export Reports Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  📥 Export Reports
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Select the reports you'd like to download. All reports include your anonymized claims analysis results.
                </p>

                <div className="space-y-4 mb-6">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReports.executiveSummary}
                      onChange={(e) => setSelectedReports({...selectedReports, executiveSummary: e.target.checked})}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Executive Summary (DOCX)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">High-level overview with key metrics and insights</div>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReports.detailedAnalysis}
                      onChange={(e) => setSelectedReports({...selectedReports, detailedAnalysis: e.target.checked})}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Detailed Analysis (CSV)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Complete dataset with all denial reasons and recommendations</div>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReports.validationReport}
                      onChange={(e) => setSelectedReports({...selectedReports, validationReport: e.target.checked})}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Validation Report (PDF)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Data quality assessment and error details</div>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReports.keyInsights}
                      onChange={(e) => setSelectedReports({...selectedReports, keyInsights: e.target.checked})}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Key Insights Dashboard (PDF)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Visual charts and top denial patterns</div>
                    </div>
                  </label>
                </div>

                <button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
                  disabled={!Object.values(selectedReports).some(v => v)}
                >
                  📥 Download Selected Reports ({Object.values(selectedReports).filter(v => v).length})
                </button>
              </div>

              {/* Share Results Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  📧 Share Results via Email
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Send reports directly to team members. We'll email all selected reports as attachments.
                </p>

                {/* Success/Error Messages */}
                {emailSuccess && (
                  <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    ✓ Results successfully shared with {sharedEmails.length} recipient(s)!
                  </div>
                )}
                {emailError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    ✗ {emailError}
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Add Email Addresses
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && emailInput.trim()) {
                          e.preventDefault();
                          if (!sharedEmails.includes(emailInput.trim())) {
                            setSharedEmails([...sharedEmails, emailInput.trim()]);
                            setEmailInput('');
                          }
                        }
                      }}
                      placeholder="colleague@example.com"
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      onClick={() => {
                        if (emailInput.trim() && !sharedEmails.includes(emailInput.trim())) {
                          setSharedEmails([...sharedEmails, emailInput.trim()]);
                          setEmailInput('');
                        }
                      }}
                      className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {sharedEmails.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Recipients ({sharedEmails.length})
                    </label>
                    <div className="space-y-2">
                      {sharedEmails.map((email, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg">
                          <span className="text-sm text-gray-900 dark:text-white">{email}</span>
                          <button
                            onClick={() => setSharedEmails(sharedEmails.filter((_, i) => i !== index))}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Message (Optional)
                  </label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={4}
                    placeholder="Add a personal message to include in the email..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <button
                  onClick={handleShareResults}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={sharedEmails.length === 0 || isSendingEmail}
                >
                  {isSendingEmail ? '📤 Sending...' : `📧 Send via Email to ${sharedEmails.length} Recipient${sharedEmails.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          )}

          {/* Next Steps View */}
          {currentView === 'nextsteps' && (
            <div className="space-y-8">
              {/* Trial Statistics */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                    7 days
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Trial Remaining</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                    8,547
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Claims Processed</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                    96.5%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Success Rate</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="text-3xl font-bold text-amber-600 dark:text-amber-400 mb-2">
                    $125K
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Potential Savings</div>
                </div>
              </div>

              {/* Data Retention */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  🗂️ Data Retention Preferences
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Choose how long you'd like us to keep your trial data. You can always export reports before deletion.
                </p>

                <div className="space-y-3">
                  <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="dataRetention"
                      value="keep30days"
                      checked={dataRetention === 'keep30days'}
                      onChange={(e) => setDataRetention(e.target.value)}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">Keep for 30 days</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Standard retention period after trial ends</div>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="dataRetention"
                      value="keep60days"
                      checked={dataRetention === 'keep60days'}
                      onChange={(e) => setDataRetention(e.target.value)}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">Keep for 60 days</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Extended retention for evaluation and comparison</div>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="dataRetention"
                      value="keep_until_conversion"
                      checked={dataRetention === 'keep_until_conversion'}
                      onChange={(e) => setDataRetention(e.target.value)}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">Keep until conversion to paid plan</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Retain data if you plan to upgrade</div>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="dataRetention"
                      value="delete_now"
                      checked={dataRetention === 'delete_now'}
                      onChange={(e) => setDataRetention(e.target.value)}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">Delete immediately after trial</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Remove all data when trial expires</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Plan Selection */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  💼 Choose Your Plan
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Ready to continue? Select a plan that fits your organization's needs.
                </p>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  {/* Starter Plan */}
                  <div
                    onClick={() => setSelectedPlan('starter')}
                    className={`cursor-pointer border-2 rounded-lg p-6 transition-all ${
                      selectedPlan === 'starter'
                        ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Starter</h3>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                      $499<span className="text-lg text-gray-600 dark:text-gray-400">/mo</span>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <li>✓ Up to 10K claims/month</li>
                      <li>✓ Basic denial analytics</li>
                      <li>✓ Standard reports</li>
                      <li>✓ Email support</li>
                    </ul>
                  </div>

                  {/* Professional Plan */}
                  <div
                    onClick={() => setSelectedPlan('professional')}
                    className={`cursor-pointer border-2 rounded-lg p-6 transition-all relative ${
                      selectedPlan === 'professional'
                        ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="absolute -top-3 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      RECOMMENDED
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Professional</h3>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                      $999<span className="text-lg text-gray-600 dark:text-gray-400">/mo</span>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <li>✓ Up to 50K claims/month</li>
                      <li>✓ Advanced AI insights</li>
                      <li>✓ Custom reports</li>
                      <li>✓ Priority support</li>
                      <li>✓ API access</li>
                    </ul>
                  </div>

                  {/* Enterprise Plan */}
                  <div
                    onClick={() => setSelectedPlan('enterprise')}
                    className={`cursor-pointer border-2 rounded-lg p-6 transition-all ${
                      selectedPlan === 'enterprise'
                        ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Enterprise</h3>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                      Custom
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <li>✓ Unlimited claims</li>
                      <li>✓ Dedicated AI models</li>
                      <li>✓ White-label reports</li>
                      <li>✓ 24/7 phone support</li>
                      <li>✓ Custom integrations</li>
                      <li>✓ SLA guarantees</li>
                    </ul>
                  </div>
                </div>

                {/* Success/Error Messages */}
                {saveSuccess && (
                  <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    ✓ Settings saved successfully!
                  </div>
                )}
                {saveError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    ✗ {saveError}
                  </div>
                )}

                <div className="flex space-x-4">
                  <button
                    onClick={handleSaveSettings}
                    disabled={isSavingSettings}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {isSavingSettings ? 'Saving...' : 'Save Settings'}
                  </button>
                  <button className="flex-1 border-2 border-primary-600 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 py-3 px-6 rounded-lg font-medium transition-colors">
                    Continue Trial
                  </button>
                  <button className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 px-6 rounded-lg font-medium transition-colors">
                    Convert to {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Link
              to="/cdi-trial/step-2"
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              ← Previous Step
            </Link>
            <Link
              to="/cdi-trial"
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              Complete Trial ✓
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3Page;
